import { getFinancialHeadersForDebtSchedule } from '../../../utils/dateUtils';
import { filterFinancialHeadersWithoutClassificationName, isProvisionalHeader } from '../../../store/investor/constants';
import { safeToNumber } from '../../../utils/utils';
import { OnePagerContext } from './context';
import { OnePagerSectionConfig } from '../editor/dealSummaryEditorInitializer';
import { FOOTNOTE_FONT_SIZE, MUTED_TEXT_COLOR } from '../editor/dealSummaryEditorConstants';
import { formatCompanyMetricsValue } from './helpers';
import { Cell, h2, p, spacer, table, td, text, th, tr, TrRow } from './plateNodes';

export function buildFinancialSummarySection(ctx: OnePagerContext, section: OnePagerSectionConfig, auditedSuffix: string): any[] | null {
    const {
        audited_financial_profit_and_loss,
        audited_financial_balance_sheet,
        audited_financial_unsecured_loan,
        companyMetricsData,
        companyMetricsLastUpdatedDate,
        encumberedCashBalance,
    } = ctx;

    const financialRows = [
        { key: 'Revenue', companyFinancialsKey: 'annualRevenue', label: 'Revenue', table: 'pnl' },
        { key: 'EBITDA', companyFinancialsKey: 'ebitda', label: 'EBITDA', table: 'pnl' },
        { key: 'Profit After Tax', companyFinancialsKey: 'pat', label: 'Profit After Tax', table: 'pnl' },
        { key: 'empty-row', label: '', table: '' },
        { key: 'Net Worth', companyFinancialsKey: 'netWorth', label: 'Net Worth', table: 'balanceSheet' },
        { key: 'Long Term Borrowings', companyFinancialsKey: 'longTermDebt', label: 'Long Term Debt', table: 'unsecuredLoans' },
        { key: 'Short Term Borrowings', companyFinancialsKey: 'shortTermDebt', label: 'Short Term Debt', table: 'unsecuredLoans' },
        {
            key: 'Cash And Cash Equivalents',
            companyFinancialsKey: 'cashAndCashEquivalents',
            label: 'Cash And Cash Equivalents',
            table: 'balanceSheet',
        },
        { key: 'Net Working Capital', companyFinancialsKey: 'workingCapitalGap', label: 'Net Working Capital', table: 'balanceSheet' },
        { key: 'Trade Receivables', companyFinancialsKey: 'tradeReceivables', label: '\tTrade Receivables', table: 'balanceSheet' },
        { key: 'Inventories', companyFinancialsKey: 'inventory', label: '\tInventory', table: 'balanceSheet' },
        { key: 'Trade Payables', companyFinancialsKey: 'tradePayables', label: '\tTrade Payables', table: 'balanceSheet' },
        { key: 'Total Assets', companyFinancialsKey: 'totalAssets', label: 'Total Balance Sheet Size', table: 'balanceSheet' },
    ];

    const FINANCIAL_HEADERS = getFinancialHeadersForDebtSchedule(companyMetricsLastUpdatedDate);

    const headerRow: TrRow = tr((FINANCIAL_HEADERS ?? []).map((header) => th([text(`${header?.name ?? '-'}`)])));

    const dataRows: TrRow[] = (financialRows ?? []).map((financialRow) => {
        const isEmptyRow = financialRow?.key === 'empty-row';

        let data: any;
        switch (financialRow?.table) {
            case 'pnl':
                data = audited_financial_profit_and_loss?.[financialRow?.key];
                break;
            case 'unsecuredLoans':
                data = audited_financial_unsecured_loan?.rows?.find((r: any) => r?.parent === financialRow?.key);
                break;
            case 'balanceSheet':
                data = audited_financial_balance_sheet?.[financialRow?.key];
                break;
        }

        const cells: Cell[] = [td([text(financialRow?.label)])];

        FINANCIAL_HEADERS?.filter?.(filterFinancialHeadersWithoutClassificationName)?.forEach((header) => {
            const isProvisionalHeaderValue = isProvisionalHeader(header);
            const isCashAndCashEquivalentsRow = financialRow?.key === 'Cash And Cash Equivalents';

            const getText = () => {
                let companyMetricsValue;
                if (isProvisionalHeaderValue) {
                    if (isCashAndCashEquivalentsRow) {
                        const encumberedCash = safeToNumber(companyMetricsData?.encumberedCashBalance);
                        const cashBalance = safeToNumber(companyMetricsData?.cashBalance);
                        const totalCash = encumberedCash + cashBalance;
                        companyMetricsValue = formatCompanyMetricsValue(totalCash);
                        return `${companyMetricsValue}`;
                    }
                    companyMetricsValue = formatCompanyMetricsValue(companyMetricsData?.[financialRow?.companyFinancialsKey as string]);
                    return `${companyMetricsValue}`;
                }
                if (isEmptyRow) return '';
                return `${data?.[header?.key] ?? '-'}`;
            };

            cells.push(td([text(getText())]));
        });

        return tr(cells);
    });

    const financialTable = table([headerRow, ...dataRows]);

    const blocks: any[] = [h2([text(`${section.label}${auditedSuffix}`)]), financialTable];

    if (encumberedCashBalance) {
        blocks.push(
            p([
                text(
                    `** Out of the total cash and cash equivalents, encumbered cash balance is ${formatCompanyMetricsValue(
                        encumberedCashBalance,
                    )} Cr`,
                    { color: MUTED_TEXT_COLOR, fontSize: FOOTNOTE_FONT_SIZE },
                ),
            ]),
        );
    }

    blocks.push(spacer());
    blocks.push(
        p([
            text(
                `*Note: Provisional metrics are updated ${
                    companyMetricsLastUpdatedDate ? 'as of ' + companyMetricsLastUpdatedDate : ''
                } basis unaudited data.`,
                { color: MUTED_TEXT_COLOR, fontSize: FOOTNOTE_FONT_SIZE },
            ),
        ]),
    );
    blocks.push(spacer());

    return blocks;
}
